import { AdminUserService, PAGE_SIZE } from "../../../services/admin-user.service.js";
import { InAppNotificationService } from "../../../services/in-app-notification.service.js";
import { PaginationControls } from "../../../components/common/PaginationControls.js";

// State
let state = {
    users: [],
    currentPage: 1,
    totalCount: 0,
    cursors: [], // Array to store the 'lastVisible' doc of each page. Index 0 = End of Page 1.
    isLoading: false,
    searchQuery: ''
};

export const UsersTab = async (container) => {
    // 1. Initial Render (Skeleton)
    if (!container.querySelector('#users-tab-content')) {
        container.innerHTML = `
            <div id="users-tab-content" class="animate-fade-in">
                <!-- Header & Actions -->
                <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div>
                        <h2 class="text-2xl font-black text-gray-900 flex items-center gap-2">
                            <span class="material-symbols-outlined text-indigo-600">group</span> User Management
                        </h2>
                        <p class="text-xs text-gray-500 font-mono mt-1">TOTAL USERS: <span id="total-user-count" class="font-bold text-indigo-600">Loading...</span></p>
                    </div>

                    <div class="flex items-center gap-2 w-full md:w-auto">
                        <!-- Search Input -->
                        <div class="relative flex-1 md:flex-initial">
                            <input type="text" id="user-search" placeholder="Search email (prefix)..." 
                                class="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 w-full md:w-64"
                                value="${state.searchQuery}">
                            <span class="material-symbols-outlined absolute left-2.5 top-2 text-gray-400 text-lg">search</span>
                        </div>
                    </div>
                </div>

                <!-- Content Area -->
                <div id="users-list-container" class="space-y-4 min-h-[400px]">
                    <!-- Users List will be injected here -->
                    <div class="flex justify-center py-20">
                        <span class="loader-spin w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></span>
                    </div>
                </div>

                <!-- Pagination -->
                <div id="users-tab-pagination" class="mt-6 flex justify-center"></div>
                <div id="pagination-debug" class="text-center text-xs text-red-500 mt-2 font-mono"></div>
            </div>
        `;

        // Bind Search
        const searchInput = container.querySelector('#user-search');
        let searchTimeout;
        searchInput.addEventListener('input', (e) => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
                const val = e.target.value.trim();
                if (val !== state.searchQuery) {
                    state.searchQuery = val;
                    state.currentPage = 1;
                    state.cursors = [];
                    loadData(container);
                }
            }, 500); // Debounce
        });


    }

    // 2. Load and Render Data
    await loadData(container);
};

const loadData = async (container) => {
    const listContainer = container.querySelector('#users-list-container');
    const totalCountEl = container.querySelector('#total-user-count');

    // [FIX] Use document.getElementById to bypass any container scope issues
    const paginationContainer = document.getElementById('users-tab-pagination');

    console.log('[UsersTab] loadData Container:', container);

    if (!paginationContainer) {
        console.error('[UsersTab] CRITICAL: Pagination Container NOT FOUND with ID users-tab-pagination');
        if (container && container.innerHTML) {
            console.log('[UsersTab] Container HTML:', container.innerHTML.substring(0, 500));
        }
    } else {
        console.log('[UsersTab] Pagination Container Found:', paginationContainer);
    }

    state.isLoading = true;
    listContainer.style.opacity = '0.5';

    try {
        // A. Fetch Data
        // Decide cursor: If page 1, null. If page > 1, use cursor from cursors[page-2]
        const cursor = state.currentPage === 1 ? null : state.cursors[state.currentPage - 2];

        // Parallel fetch for Count (if needed) and Data
        const promises = [
            AdminUserService.fetchUsers(cursor, 'next', state.searchQuery)
        ];

        // Only fetch total count on first load or search change (optimization)
        if (state.totalCount === 0 || state.currentPage === 1) {
            promises.push(AdminUserService.getTotalUserCount(state.searchQuery));
        }

        const [dataResult, countResult] = await Promise.all(promises);

        if (countResult !== undefined) {
            state.totalCount = countResult;
            totalCountEl.innerText = countResult.toLocaleString();
        }

        state.users = dataResult.users;

        // Update Cursors Stack
        if (dataResult.lastVisible) {
            // Store the cursor for the END of the current page.
            state.cursors[state.currentPage - 1] = dataResult.lastVisible;
        }

        // B. Render List
        renderList(listContainer);

        // C. Render Pagination
        // C. Render Pagination
        const pageSize = PAGE_SIZE || 20;
        const totalPages = Math.ceil(state.totalCount / pageSize) || 1;

        console.log(`[UsersTab] Total: ${state.totalCount}, Size: ${pageSize}, Pages: ${totalPages}`);

        // Force render debug info
        const debugContainer = document.getElementById('pagination-debug');
        if (debugContainer) {
            debugContainer.innerText = `Debug: Users=${state.totalCount}, Size=${pageSize}, Pages=${totalPages}, Current=${state.currentPage}`;
            debugContainer.style.display = 'block';
        }

        PaginationControls.render({
            target: paginationContainer,
            currentPage: state.currentPage,
            totalPages: totalPages,
            onPageChange: (newPage) => {
                state.currentPage = newPage;
                loadData(container);
            }
        });

    } catch (e) {
        console.error(e);
        listContainer.innerHTML = `
            <div class="bg-red-50 border border-red-200 text-red-600 p-6 rounded-xl text-center">
                <span class="material-symbols-outlined text-3xl mb-2">error</span>
                <p>Failed to load users.</p>
                <p class="text-xs opacity-70">${e.message}</p>
            </div>
        `;
    } finally {
        state.isLoading = false;
        listContainer.style.opacity = '1';
    }
};

const renderList = (target) => {
    if (state.users.length === 0) {
        target.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <span class="material-symbols-outlined text-4xl mb-2">search_off</span>
                <p class="text-sm">No users found.</p>
            </div>
        `;
        return;
    }

    // Desktop Table Wrapper
    let html = `
        <div class="bg-white shadow-sm border border-gray-100 rounded-xl overflow-hidden">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-12">No.</th>
                            <th class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                            <th class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                            <th class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Joined</th>
                            <th class="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200 text-sm">
    `;

    state.users.forEach((user, index) => {
        const isSuper = user.role === 'super_admin' || user.email === 'hegler.kim@gmail.com';
        const roleClass = getRoleBadgeClass(user.role);
        const joinDate = user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : '-';
        const globalIndex = ((state.currentPage - 1) * 20) + index + 1;

        // Banned Styling
        const isBanned = user.status === 'banned';
        const rowClass = isBanned ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50 transition-colors';
        const opacityClass = isBanned ? 'opacity-75' : '';

        html += `
            <tr class="${rowClass} transition-colors">
                <td class="px-6 py-4 whitespace-nowrap text-xs text-indigo-500 font-bold font-mono ${opacityClass}">
                    ${globalIndex || index + 1}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                        <div class="h-8 w-8 rounded-full ${isBanned ? 'bg-red-200 text-red-700' : 'bg-indigo-100 text-indigo-700'} flex items-center justify-center font-bold shrink-0">
                            ${user.photoURL ? `<img src="${user.photoURL}" class="h-8 w-8 rounded-full ${isBanned ? 'grayscale' : ''}">` : user.email ? user.email[0].toUpperCase() : 'U'}
                        </div>
                        <div class="ml-4">
                            <div class="text-sm font-bold ${isBanned ? 'text-red-800 line-through' : 'text-gray-900'}">${processEmail(user.email)}</div>
                            <div class="text-xs text-gray-400 font-mono">${user.id}</div>
                            ${isBanned ? '<span class="text-[10px] text-red-600 font-bold uppercase tracking-wider">BANNED</span>' : ''}
                        </div>
                    </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap ${opacityClass}">
                    <span class="px-2 py-0.5 inline-flex text-xs leading-5 font-bold rounded-full uppercase ${roleClass}">
                        ${user.role || 'user'}
                        ${isSuper ? '<span class="ml-1 text-red-500">★</span>' : ''}
                    </span>
                    ${user.badges ? user.badges.map(b => `<span class="ml-1 text-[10px]" title="${b}">🏅</span>`).join('') : ''}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-gray-500 text-xs ${opacityClass}">
                    ${joinDate}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    ${!isSuper ? `
                        <div class="flex justify-end gap-2">
                            ${isBanned ? `
                                <button class="bg-white border border-red-200 text-red-600 hover:bg-red-600 hover:text-white px-3 py-1 rounded text-xs font-bold transition-all shadow-sm btn-unban"
                                    data-id="${user.id}" data-email="${user.email}">
                                    RESTORE (UNBAN)
                                </button>
                            ` : `
                                <button class="text-amber-500 hover:bg-amber-50 p-1 rounded transition-colors btn-medal" 
                                    data-id="${user.id}" data-email="${user.email}" title="Award Medal">
                                    <span class="material-symbols-outlined text-lg">military_tech</span>
                                </button>
                                <button class="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 p-1 rounded transition-colors btn-role" 
                                    data-id="${user.id}" data-current="${user.role || 'user'}" title="Change Role">
                                    <span class="material-symbols-outlined text-lg">manage_accounts</span>
                                </button>
                            `}
                        </div>
                    ` : '<span class="text-xs text-gray-300 italic">Protected</span>'}
                </td>
            </tr>
        `;
    });

    html += `</tbody></table></div></div>`;
    target.innerHTML = html;

    // Actions Binding
    target.querySelectorAll('.btn-medal').forEach(btn => {
        btn.onclick = (e) => handleMedal(e.currentTarget.dataset.id, e.currentTarget.dataset.email);
    });

    target.querySelectorAll('.btn-role').forEach(btn => {
        btn.onclick = (e) => handleRole(e.currentTarget.dataset.id, e.currentTarget.dataset.current);
    });

    // Unban Handler
    target.querySelectorAll('.btn-unban').forEach(btn => {
        btn.onclick = async (e) => {
            const uid = e.currentTarget.dataset.id;
            const email = e.currentTarget.dataset.email;
            if (confirm(`User: ${email}\n\n정말로 이 사용자의 차단을 해제하시겠습니까?`)) {
                try {
                    await AdminUserService.unbanUser(uid);
                    window.Modal.toast("사용자 차단이 해제되었습니다.");
                    // Optimistic update
                    const u = state.users.find(u => u.id === uid);
                    if (u) u.status = 'active';
                    renderList(target);
                } catch (err) {
                    window.Modal.alert("Unban Failed: " + err.message);
                }
            }
        };
    });
};

const getRoleBadgeClass = (role) => {
    switch (role) {
        case 'admin': return 'bg-purple-100 text-purple-800';
        case 'super_admin': return 'bg-red-100 text-red-800 border border-red-200';
        case 'author': return 'bg-blue-100 text-blue-800';
        default: return 'bg-gray-100 text-gray-800';
    }
};

const processEmail = (email) => {
    if (!email) return 'Unknown User';
    return email;
};

// --- Handlers ---
const handleRole = async (uid, currentRole) => {
    // [FIX] Use SweetAlert2 Select for better UX
    const { value: newRole } = await Swal.fire({
        title: 'Change User Role',
        input: 'select',
        inputOptions: {
            'user': 'User (General)',
            'author': 'Author (Writer)',
            'admin': 'Admin (Manager)'
        },
        inputValue: currentRole,
        showCancelButton: true,
        confirmButtonText: 'Update Role',
        showLoaderOnConfirm: true,
        preConfirm: async (role) => {
            if (role === currentRole) return false;
            try {
                await AdminUserService.updateRole(uid, role);
                return role;
            } catch (error) {
                Swal.showValidationMessage(`Request failed: ${error}`);
            }
        }
    });

    if (newRole && newRole !== currentRole) {
        window.Modal.toast(`Role Updated to ${newRole.toUpperCase()}!`);

        // Send Notification
        await InAppNotificationService.createNotification(
            uid,
            'system',
            `회원님의 등급이 [${newRole.toUpperCase()}]로 변경되었습니다.`,
            null, // From System
            '/mypage'
        );

        // Optimistic Update
        const u = state.users.find(u => u.id === uid);
        if (u) u.role = newRole;
        renderList(document.querySelector('#users-list-container'));
    }
};

const handleMedal = async (uid, email) => {
    // [FIX] Standardized Badge List
    const badges = {
        'best_reviewer': '✍️ Best Reviewer (베스트 리뷰어)',
        'top_author': '🏆 Top Author (인기 작가)',
        'vip': '👑 VIP (우수 회원)',
        'donation_king': '💖 Donation King (후원왕)',
        'hall_of_fame': '🏛️ Hall of Fame (명예의 전당)'
    };

    const { value: badgeId } = await Swal.fire({
        title: 'Award Medal (Badge)',
        text: `Target: ${email}`,
        input: 'select',
        inputOptions: badges,
        inputPlaceholder: 'Select a badge to award',
        showCancelButton: true,
        confirmButtonText: 'Award Medal',
        showLoaderOnConfirm: true,
        preConfirm: async (selectedBadge) => {
            if (!selectedBadge) return false;
            try {
                await AdminUserService.awardBadge(uid, selectedBadge);
                return selectedBadge;
            } catch (error) {
                Swal.showValidationMessage(`Request failed: ${error}`);
            }
        }
    });

    if (badgeId) {
        const badgeName = badges[badgeId]?.split('(')[0].trim() || 'Medal';
        window.Modal.toast("Medal Awarded!");

        // Send Notification
        await InAppNotificationService.createNotification(
            uid,
            'system',
            `축하합니다! 운영진으로부터 [${badgeName}] 훈장을 수여받으셨습니다!`,
            null, // From System
            '/mypage'
        );

        // Refresh (Optimistic)
        const u = state.users.find(u => u.id === uid);
        if (u) {
            if (!u.badges) u.badges = [];
            u.badges.push(badgeId);
        }
        renderList(document.querySelector('#users-list-container'));
    }
};

